/**
 * maintenance.js — small helper for the sitewide maintenance curtain.
 *
 * When REACT_APP_MAINTENANCE_MODE === "true" (build-time CRA env var),
 * every non-allowlisted route renders the maintenance screen so we can
 * drop an emergency curtain on the public site without touching code.
 *
 * The allowlist keeps admin ops accessible during a curtain:
 *   /admin              — admin console
 *   /admin/*            — any nested admin route
 *   /login              — admins need this to reach /admin
 *   /auth/callback      — Google OAuth handoff (fragment-routed)
 *   /health             — infra health probes (harmless placeholder)
 *
 * Flipping the flag in production:
 *   1. Set REACT_APP_MAINTENANCE_MODE=true in the Emergent Secrets panel
 *   2. Republish (CRA env vars are baked at build time)
 *   3. To lift the curtain: set it back to false and republish again
 *
 * The `isMaintenanceOn` value is computed once at module load — CRA
 * inlines env vars at build, so there's no reason to re-check per render.
 */

export const isMaintenanceOn =
  String(process.env.REACT_APP_MAINTENANCE_MODE || "").toLowerCase() === "true";

const ALLOWLIST_EXACT = new Set(["/login", "/auth/callback", "/health"]);
const ALLOWLIST_PREFIXES = ["/admin"];

/**
 * pathIsAllowlisted — does this pathname bypass the curtain?
 * Case-insensitive on the prefix so `/Admin` isn't a foot-gun.
 */
export const pathIsAllowlisted = (pathname) => {
  if (!pathname) return false;
  const p = pathname.toLowerCase();
  if (ALLOWLIST_EXACT.has(p)) return true;
  return ALLOWLIST_PREFIXES.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`)
  );
};
